import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Info, Loader2, Check, Trash2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SERVICES = [["sale", "Sale"], ["purchase", "Purchase"], ["tenancy", "Tenancy"], ["maintenance", "Maintenance"]];
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

export default function Products() {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("");
  const [service, setService] = useState("");
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({});
  const [dealValue, setDealValue] = useState(null);
  const [askDeal, setAskDeal] = useState(false);
  const [pendingVar, setPendingVar] = useState(null);
  const [basket, setBasket] = useState([]);
  const [availability, setAvailability] = useState({});

  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/service-availability").then((r) => setAvailability(r.data.availability || {})).catch(() => {}); }, []);
  useEffect(() => { setService(""); }, [propId]);
  useEffect(() => {
    if (service) { api.get(`/products?service=${service}`).then((r) => setProducts(r.data)).catch(() => {}); }
    else setProducts([]);
    setSelected({}); setDealValue(null);
  }, [service]);

  const selectedProp = properties.find((p) => p.id === propId);
  const propCity = selectedProp?.city;
  const cityAvail = (propCity && availability[propCity]) || {};
  const serviceEnabled = (key) => !!propId && !!cityAvail[key];

  const priceOf = (p) => (p.price_type === "fixed" ? (p.price || 0) : (dealValue != null ? (p.percentage / 100) * dealValue : null));

  const toggle = (p) => {
    const on = !!selected[p.id];
    if (!on && p.price_type === "variable" && dealValue == null) { setPendingVar(p); setAskDeal(true); return; }
    setSelected((s) => ({ ...s, [p.id]: !on }));
  };
  const confirmDeal = (val) => {
    setDealValue(val);
    if (pendingVar) setSelected((s) => ({ ...s, [pendingVar.id]: true }));
    setPendingVar(null); setAskDeal(false);
  };

  const addToBasket = () => {
    if (!propId) { toast.error("Choose a property first"); return; }
    const items = products.filter((p) => selected[p.id]).map((p) => ({ id: p.id, name: p.name, service, price_type: p.price_type, amount: priceOf(p), deal_value: p.price_type === "variable" ? dealValue : null }));
    if (items.length === 0) { toast.error("Select at least one product"); return; }
    if (items.some((i) => i.amount == null)) { toast.error("Please provide the deal value"); return; }
    const prop = properties.find((x) => x.id === propId);
    setBasket((b) => [...b, ...items.map((i) => ({ ...i, key: `${i.id}-${Date.now()}-${Math.random()}`, property_id: propId, property_address: prop?.address || "" }))]);
    setService(""); setProducts([]); setSelected({}); setDealValue(null);
    toast.success("Added to basket");
  };

  const removeItem = (key) => setBasket((b) => b.filter((i) => i.key !== key));
  const total = basket.reduce((s, i) => s + (i.amount || 0), 0);
  const checkout = async () => {
    if (basket.length === 0) { toast.error("Your basket is empty"); return; }
    try {
      const { data } = await api.post("/payments/checkout", { items: basket.map((i) => ({ product_id: i.id, deal_value: i.deal_value || null, property_id: i.property_id || null })), origin_url: window.location.origin });
      if (!data.checkout_url) { toast.error("Could not start checkout. Please try again."); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed"); }
  };

  const field = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

  return (
    <div className="max-w-5xl" data-testid="products-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2"><ShoppingBag className="h-7 w-7 text-emerald-600" /> Products</h1>
      <p className="mt-2 text-sm text-slate-500">Choose a property, pick a service and add the products you need to your basket.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <label className="text-sm font-medium text-slate-700">Property</label>
            <select data-testid="product-property-select" value={propId} onChange={(e) => setPropId(e.target.value)} className={`mt-1.5 ${field}`}>
              <option value="">Select one of your properties…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.address}</option>)}
            </select>
            <div className="mt-4">
              <span className="text-sm font-medium text-slate-700">Service</span>
              {!propId && <p className="text-xs text-slate-400 mt-1" data-testid="service-hint">Select a property first to choose a service.</p>}
              <div className="mt-2 flex flex-wrap gap-4">
                {SERVICES.map(([key, label]) => {
                  const enabled = serviceEnabled(key);
                  const showInfo = !!propId && !enabled;
                  return (
                    <label key={key} className={`flex items-center gap-1.5 text-sm ${enabled ? "text-slate-700 cursor-pointer" : "text-slate-400 cursor-not-allowed"}`}>
                      <input type="radio" name="service" data-testid={`service-radio-${key}`} disabled={!enabled} checked={service === key} onChange={() => enabled && setService(key)} /> {label}
                      {showInfo && (
                        <span className="group relative inline-flex" data-testid={`service-unavailable-${key}`}>
                          <Info className="h-3.5 w-3.5 text-slate-400" />
                          <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 hidden group-hover:block w-44 bg-slate-900 text-white text-xs rounded-lg p-2 z-10 shadow-lg text-center">not available at this location</span>
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {service && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="product-list">
              {products.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6">No products available for this service yet.</div>
              ) : products.map((p) => {
                const amt = priceOf(p);
                return (
                  <label key={p.id} data-testid="product-option" className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 cursor-pointer">
                    <input type="checkbox" data-testid={`product-check-${p.id}`} checked={!!selected[p.id]} onChange={() => toggle(p)} className="h-4 w-4" />
                    <span className="flex-1 min-w-0 flex items-center gap-1.5">
                      <span className="text-sm text-slate-800">{p.name}</span>
                      <span className="group relative inline-flex">
                        <Info className="h-3.5 w-3.5 text-slate-400" data-testid="product-info-icon" />
                        <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 hidden group-hover:block w-56 bg-slate-900 text-white text-xs rounded-lg p-2 z-10 shadow-lg">{p.details || "No details provided."}</span>
                      </span>
                    </span>
                    <span className="text-sm font-mono text-slate-700 shrink-0">{p.price_type === "fixed" ? inr(p.price) : (amt != null ? `${inr(amt)} (${p.percentage}%)` : `${p.percentage}% of deal`)}</span>
                  </label>
                );
              })}
              {products.length > 0 && (
                <button onClick={addToBasket} data-testid="add-to-basket-button" className={`${BTN} mt-4 px-5 py-2.5`}>Add to basket</button>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-fit" data-testid="basket-panel">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-emerald-600" /> Basket</h2>
          {basket.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Your basket is empty.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {basket.map((i) => (
                <div key={i.key} data-testid="basket-item" className="flex items-start gap-2 text-sm border-b border-slate-100 pb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-800">{i.name}</div>
                    <div className="text-xs text-slate-400 capitalize truncate">{i.service} · {i.property_address}</div>
                  </div>
                  <span className="font-mono text-slate-700">{inr(i.amount)}</span>
                  <button onClick={() => removeItem(i.key)} data-testid="basket-remove-button" className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 font-semibold text-slate-900">
                <span>Total</span><span className="font-mono" data-testid="basket-total">{inr(total)}</span>
              </div>
            </div>
          )}
          <button onClick={checkout} data-testid="checkout-button" className={`${BTN} w-full justify-center mt-4 px-5 py-2.5 disabled:opacity-60`} disabled={basket.length === 0}>Checkout</button>
        </div>
      </div>

      {askDeal && <DealValueModal onClose={() => { setAskDeal(false); setPendingVar(null); }} onConfirm={confirmDeal} />}
    </div>
  );
}

function DealValueModal({ onClose, onConfirm }) {
  const [val, setVal] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="deal-value-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Deal / transaction value</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">This value is used to calculate percentage-based products for this service.</p>
        <input type="number" autoFocus data-testid="deal-value-input" value={val} onChange={(e) => setVal(e.target.value)}
          className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" placeholder="e.g. 5000000" />
        <button onClick={() => { const n = Number(val); if (!n || n <= 0) { toast.error("Enter a valid amount"); return; } onConfirm(n); }}
          data-testid="deal-value-confirm" className={`${BTN} w-full justify-center mt-4 px-5 py-2.5`}><Check className="h-4 w-4" /> Confirm</button>
      </div>
    </div>
  );
}
